import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { fetchCustomerData } from '../../redux/slices/customerSlice';
import { fetchRepsData } from '../../redux/slices/repSlice';
import { fetchBankData } from '../../redux/slices/bankSlice';
import { useDispatch, useSelector } from 'react-redux';
import { BASE_URL, apiRequest } from '../../utils/api';
import moment from 'moment';
import ErrorScreen from '../../components/ErrorScreen';
import Loader from '../../components/Loader';
import ReactToPrint, { useReactToPrint } from 'react-to-print';
import PaymentPrint from '../../print/PaymentPrint';
import CloseIcon from '@mui/icons-material/Close';
import ModalCustom from '../../components/modals/ModalCustom';
import AddNew from './AddNew';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ModalConfirmation from '../../components/modals/ModalConfirmation';
import Edit from './Edit';
import DataTable from './DataTable';
import { exportToExcel } from '../../utils/exportToExcel';
import { fetchVendorData } from '../../redux/slices/vendorSlice';

export default function Payments() {
    const dispatch = useDispatch();
    let { user } = useSelector((state) => state?.user);

    const { data: banks, isLoading: isLoadingBanks, error: errorBanks } = useSelector((state) => state.bank);
    const { data: customers, isLoading: isLoadingCustomers, error: errorCustomers } = useSelector((state) => state.customer);
    const { data: reps, isLoading: isLoadingReps, error: errorReps } = useSelector((state) => state.rep);
    const { data: vendors, isLoading: isLoadingVendors, error: errorVendors } = useSelector((state) => state.vendor);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [loadingDelReq, setLoadingDelReq] = useState(false);

    const [payment, setPayment] = useState(null);
    const [totalAmount, setTotalAmount] = useState(0);

    const [selectedRep, setSelectedRep] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [selectedBank, setSelectedBank] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState([]);

    const [selectedSlip, setSelectedSlip] = useState({});

    const curDate = new Date();
    const [startDate, setStartDate] = useState(
        new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate() - 2).toLocaleDateString('en-CA')
    );
    const [endDate, setEndDate] = useState(new Date().toLocaleDateString('en-CA'));
    const [verifiedStartDate, setVerifiedStartDate] = useState('');
    const [verifiedEndDate, setVerifiedEndDate] = useState('');

    const [amountLessThan, setAmountLessThan] = useState('');
    const [amountGreaterThan, setAmountGreaterThan] = useState('');
    const [amountEqualTo, setAmountEqualTo] = useState('');
    const [refNo, setRefNo] = useState('');

    const [payments, setPayments] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleStartDate = (e) => {
        setStartDate(e?.target?.value);
    };

    const handleEndDate = (e) => {
        setEndDate(e?.target?.value);
    };

    useEffect(() => {
        if (!customers) {
            dispatch(fetchCustomerData());
        }
        if (!reps) {
            dispatch(fetchRepsData());
        }
        if (!banks) {
            dispatch(fetchBankData());
        }
        if (!vendors) {
            dispatch(fetchVendorData());
        }

        return () => {
            setSelectedCustomer(null);
            setSelectedRep(null);
            setSelectedBank(null);
            setSelectedVendor(null);
        };
    }, []);

    const handleFilter = async () => {
        setIsLoading(true);
        setPayments(null);
        setError(null);

        let bankIds = selectedBank?.map((i) => i?.id);
        let repIds = selectedRep?.map((i) => i?.id);
        let customerIds = selectedCustomer?.map((i) => i?.id);
        let vendorIds = selectedVendor?.map((i) => i?.id);

        let data = {
            bankId: bankIds?.length > 0 ? bankIds : null,
            vendorIds: vendorIds?.length > 0 ? vendorIds : null,
            repId: repIds?.length > 0 ? repIds : null,
            customerId: customerIds?.length > 0 ? customerIds : null,
            startDate: startDate,
            endDate: endDate,
            ...(verifiedStartDate && { verifiedStartDate }),
            ...(verifiedEndDate && { verifiedEndDate }),
            paymentVerified: user?.role === 'cash-payments' ? false : true,
            ...(amountLessThan && { amountLessThan }),
            ...(amountGreaterThan && { amountGreaterThan }),
            ...(amountEqualTo && { amountEqualTo }),
            ...(refNo && { refNo })
        };

        try {
            const res = await apiRequest('post', `payments/byFilter`, data);

            setPayments(res?.data);
            setTotalAmount(res?.data?.reduce((total, item) => total + item.amount, 0));
        } catch (error) {
            setError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleFilter();

        return () => {
            setPayments(null);
            setIsLoading(true);
            setError(null);
        };
    }, []);

    const componentRef = React.useRef(null);
    const handlePrint = useReactToPrint({
        content: () => componentRef.current
    });

    const handleDelete = async () => {
        setLoadingDelReq(true);
        try {
            const res = await apiRequest('delete', `payments/${payment?.id}`, {});

            if (res?.data) {
                setPayment(null);
                handleFilter();
                setShowDeleteModal(false);
                return toast.success('Deleted Successfully!');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadingDelReq(false);
        }
    };

    const handleExport = async () => {
        try {
            const transformedData = payments.map((item) => ({
                Date_Created: moment(item?.createdOn).format('MMM DD, YYYY - hh:mm A'),
                Verified_At: item?.verifiedAt ? moment(item?.verifiedAt).format('MMM DD, YYYY') : '-',
                Customer: item?.customer?.name,
                Amount: item?.amount,
                Vendor: item?.vendor?.name,
                Bank: item?.bank?.name,
                Ref_No: item?.refNo,
                Comment: item?.comments,
                Rep: `${item?.user?.firstName} ${item?.user?.lastName}`
            }));

            if (!!payments) {
                exportToExcel(transformedData, `KK-Payment-${Date.now()}.xlsx`);
            }
        } catch (error) {
            return toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        }
    };

    return (
        <Layout>
            <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                <Grid item lg={10.5} sm={2} md={2} xs={6}>
                    <Typography variant="h6">Payments</Typography>
                </Grid>
                <Grid item lg={1.5} sm={3.5} md={2.5} xs={12}>
                    <Button onClick={handleExport} fullWidth variant="contained" sx={{ textTransform: 'capitalize' }}>
                        Export to Excel
                    </Button>
                </Grid>

                <Grid
                    onClick={() => setShowCreateModal(true)}
                    item
                    lg={1.5}
                    sm={3.5}
                    md={2.5}
                    xs={6}
                    sx={{ display: { xs: 'block', sm: 'none' } }}
                >
                    <Button fullWidth variant="contained">
                        Create Payment
                    </Button>
                </Grid>

                <Grid item lg={2} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={banks ?? []}
                        defaultValue={[]}
                        getOptionLabel={(b) => `${b?.name}`}
                        // value={selectedBank}
                        onChange={(event, selected) => {
                            setSelectedBank(selected);
                        }}
                        renderInput={(params) => <TextField {...params} label="Bank" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={2} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={vendors ?? []}
                        defaultValue={[]}
                        getOptionLabel={(b) => `${b?.name}`}
                        // value={selectedBank}
                        onChange={(event, selected) => {
                            setSelectedVendor(selected);
                        }}
                        renderInput={(params) => <TextField {...params} label="Vendor" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={2} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={reps ?? []}
                        defaultValue={[]}
                        getOptionLabel={(rep) => `${rep?.firstName} ${rep?.lastName} - ${rep?.phoneNo}`}
                        // value={selectedRep}
                        onChange={(event, selected) => {
                            setSelectedRep(selected);
                        }}
                        renderInput={(params) => <TextField {...params} label="Rep" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={2} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={customers ?? []}
                        defaultValue={[]}
                        getOptionLabel={(customer) => `${customer?.name} - ${customer?.phoneNo}`}
                        // value={selectedCustomer}
                        onChange={(event, selected) => {
                            setSelectedCustomer(selected);
                        }}
                        renderInput={(params) => <TextField {...params} label="Customer" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        variant="outlined"
                        label="Amount Less Than"
                        value={amountLessThan}
                        onChange={(e) => setAmountLessThan(e.target.value)}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        variant="outlined"
                        label="Amount Greater Than"
                        value={amountGreaterThan}
                        onChange={(e) => setAmountGreaterThan(e.target.value)}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        variant="outlined"
                        label="Amount Equal To"
                        value={amountEqualTo}
                        onChange={(e) => setAmountEqualTo(e.target.value)}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <TextField
                        size="small"
                        fullWidth
                        variant="outlined"
                        label="Reference Number"
                        value={refNo}
                        onChange={(e) => setRefNo(e.target.value)}
                    />
                </Grid>

                <Grid item lg={1.25} xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        variant="outlined"
                        label="Start Date"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={handleStartDate}
                    />
                </Grid>

                <Grid item lg={1.25} xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        variant="outlined"
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={handleEndDate}
                    />
                </Grid>

                <Grid item lg={1.25} xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        variant="outlined"
                        label="Verified Start"
                        InputLabelProps={{ shrink: true }}
                        value={verifiedStartDate}
                        onChange={(e) => setVerifiedStartDate(e.target.value)}
                    />
                </Grid>

                <Grid item lg={1.25} xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        variant="outlined"
                        label="Verified End"
                        InputLabelProps={{ shrink: true }}
                        value={verifiedEndDate}
                        onChange={(e) => setVerifiedEndDate(e.target.value)}
                    />
                </Grid>

                <Grid item lg={1} sm={3.5} md={2.5} xs={12}>
                    <Button onClick={handleFilter} fullWidth variant="contained" sx={{ textTransform: 'capitalize', bgcolor: 'green' }}>
                        Filter
                    </Button>
                </Grid>

                <Grid item lg={1} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Button onClick={handlePrint} fullWidth variant="contained" sx={{ textTransform: 'capitalize', bgcolor: 'green' }}>
                        Print
                    </Button>
                </Grid>

                <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                        Create Payment
                    </Button>
                </Grid>

                <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography>Total Amount: {totalAmount?.toLocaleString()}</Typography>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Created Date</TableCell>
                            <TableCell>Verified At</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Bank</TableCell>
                            <TableCell>Ref No</TableCell>
                            <TableCell>Comments</TableCell>
                            <TableCell>Rep</TableCell>
                            <TableCell>Slip</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading && (
                            <TableCell colSpan={10}>
                                <Loader />
                            </TableCell>
                        )}
                        <DataTable
                            payments={payments}
                            onSlip={(row) => setSelectedSlip({ open: true, image: row?.slip })}
                            onEdit={(row) => {
                                setPayment(row);
                                setShowEditModal(true);
                            }}
                            onDelete={(row) => {
                                setPayment(row);
                                setShowDeleteModal(true);
                            }}
                            onChangePaymentStatus={async (row) => {
                                try {
                                    const res = await apiRequest('patch', `payments/${row?.id}/status`, {
                                        paymentVerified: !row?.paymentVerified
                                    });

                                    if (res?.data) {
                                        handleFilter();
                                        return toast.success('Payment status updated successfully!');
                                    }
                                } catch (error) {
                                    toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
                                }
                            }}
                        />
                        {!!error && (
                            <TableCell colSpan={10}>
                                <ErrorScreen error={error} />
                            </TableCell>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <div style={{ display: 'none' }}>
                <PaymentPrint
                    ref={componentRef}
                    data={payments || []}
                    rep={selectedRep}
                    bank={selectedBank}
                    vendor={selectedVendor}
                    customer={selectedCustomer}
                    startDate={startDate}
                    endDate={endDate}
                />
            </div>

            <ModalCustom open={selectedSlip?.open}>
                <IconButton
                    color="inherit"
                    onClick={() => setSelectedSlip({ open: false, image: null })}
                    sx={{ position: 'absolute', top: 8, right: 5, backgroundColor: '#fff' }}
                >
                    <CloseIcon />
                </IconButton>
                <Box sx={{ ml: -2.1, mr: -2.7, mb: -1.7, mt: -3.2 }}>
                    <img src={selectedSlip?.image} alt={'select slip'} width={'100%'} />
                </Box>
            </ModalCustom>

            {showCreateModal && (
                <ModalCustom open={showCreateModal} title={'Create Payment'}>
                    <AddNew
                        onClose={(val) => {
                            setShowCreateModal(false);
                            if (val == true) {
                                handleFilter();
                            }
                        }}
                    />
                </ModalCustom>
            )}

            {showEditModal && (
                <ModalCustom open={showEditModal} title={'Edit Payment'}>
                    <Edit
                        payment={payment}
                        onClose={(val) => {
                            setShowEditModal(false);
                            if (val == true) {
                                handleFilter();
                            }
                        }}
                    />
                </ModalCustom>
            )}

            <ModalConfirmation
                open={showDeleteModal}
                loading={loadingDelReq}
                header={'Delete Payment'}
                description={`Are you sure, you want to delete the payment of customer ${payment?.customer?.name} of Rs. ${payment?.amount} dated ${moment(payment?.createdOn).format('MMM DD, YYYY - hh:mm A')}?`}
                onClickNo={() => setShowDeleteModal(false)}
                onClickYes={handleDelete}
            />
        </Layout>
    );
}
